'use client';

import React, { forwardRef, useMemo } from 'react';
import { ulid } from 'ulid';
import clsx from 'clsx';
import { Icons } from '@shared/ui';
import { type BasicShopUIPropTypes, type BuyerLayoutProps } from 'shop-ui/types';
import i18n from '@shared/i18n';
import './ProductShippingDetail.scss';
import { useBuyerProductStore } from 'app/store/product';

interface ProductShippingDetailProps extends BasicShopUIPropTypes, BuyerLayoutProps {
    // 自行加入其他 props
    content: string;
}

const previewAddressCountry = [
    {
        key: '台灣',
        value: 'TW',
    },
];

const previewShippingMethods = [
    {
        id: ulid(),
        shipping_type: 'standard',
        shipping_type_name: '基本運送（一般宅配）',
        shipping_fee: {
            value: 'NT$ 120',
            currency: 'TWD',
            amount: '120',
        },
    },
    {
        id: ulid(),
        shipping_type: 'express',
        shipping_type_name: '快捷運送',
        shipping_fee: {
            value: 'NT$ 150',
            currency: 'TWD',
            amount: '150',
        },
    },
    {
        id: ulid(),
        shipping_type: '7-11',
        shipping_type_name: '7-11 取貨',
        shipping_fee: {
            value: 'NT$ 60',
            currency: 'TWD',
            amount: '60',
        },
    },
];

export const ProductShippingDetail = forwardRef<
    HTMLDivElement,
    ProductShippingDetailProps
>((props, ref) => {
    const { className, style, isEditingMode, isBuyer } = props;

    const {
        shipTo = 'TW',
        setShipTo,
        shippingMethods: _shippingMethods,
        addressCountry: _addressCountry,
    } = useBuyerProductStore();

    const shippingMethods = useMemo(() => {
        if (isBuyer) {
            return _shippingMethods;
        }

        return previewShippingMethods;
    }, [_shippingMethods, isBuyer]);

    const addressCountry = useMemo(() => {
        if (isBuyer) {
            return _addressCountry;
        }

        return previewAddressCountry;
    }, [_addressCountry, isBuyer]);

    return (
        <section
            className={clsx('product-shipping-detail', className)}
            style={style}
            ref={ref}
        >
            <h2 className="product-shipping-detail__title">
                {i18n.gettext('物流及運費說明')}
            </h2>
            <div className="product-shipping-detail__content">
                <div className="product-ship-to">
                    <label className="product-ship-to__label">
                        {i18n.gettext('從台灣寄出，寄往：')}
                    </label>
                    <div className="product-ship-to__select-wrapper">
                        <div
                            className={clsx('product-ship-to__select', {
                                'product-ship-to__select--disabled': isEditingMode,
                            })}
                        >
                            <select
                                value={shipTo}
                                onChange={(e) => setShipTo(e.target.value)}
                                disabled={!addressCountry.length}
                            >
                                {addressCountry.map((country) => (
                                    <option
                                        value={country.value}
                                        key={country.key + country.value}
                                    >
                                        {country.key}
                                    </option>
                                ))}
                            </select>
                            <span className="product-ship-to__select-icon">
                                <Icons.csr.CaretDown size="1em" weight="bold" />
                            </span>
                        </div>
                    </div>
                </div>
                <div className="product-shipping-fee">
                    <div className="product-shipping-fee__item product-shipping-fee__item--a">
                        {i18n.gettext('提供的運送方式')}
                    </div>
                    <div className="product-shipping-fee__item product-shipping-fee__item--b">
                        {i18n.gettext('運費')}
                    </div>
                    <div className="product-shipping-fee__item product-shipping-fee__item--c">
                        <hr />
                    </div>
                    <div className="product-shipping-fee__item product-shipping-fee__item--d">
                        {shippingMethods.map((method) => (
                            <div
                                className="product-shipping-method"
                                key={method.id + 'shipping_type_name'}
                            >
                                {method.shipping_type_name}
                            </div>
                        ))}
                    </div>
                    <div className="product-shipping-fee__item product-shipping-fee__item--e">
                        {shippingMethods.map((method) => (
                            <div
                                className="product-shipping-method"
                                key={method.id + 'shipping_fee'}
                            >
                                {method.shipping_fee.value + ' ' + i18n.gettext('起')}
                            </div>
                        ))}
                    </div>
                    <div className="product-shipping-fee__item product-shipping-fee__item--f">
                        {i18n.gettext(
                            '實際運費金額以購物車結算或是到貨時收取的金額為準。'
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
});

ProductShippingDetail.displayName = 'ProductShippingDetail';
